/**
 * `/imports/$importId` — every state after the upload, chosen by the import's status.
 *
 * The query polls while the worker moves the import and stops at every status a person or
 * the end owns (`import-wizard.ts`'s `POLLED`). A static render cannot see the interval, so
 * that the poll stops is U79's observation.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { Surface } from "@packagepage/ui";
import {
  IMPORT_ERROR_KINDS,
  type ImportDetail,
  type ImportRowView,
} from "@packagepage/shared";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import {
  ImportApplying,
  ImportChecking,
  ImportClean,
  ImportDone,
  ImportEnded,
  ImportFrame,
  ImportMatching,
  ImportOverLimit,
  ImportReading,
  ImportReview,
  type ApplyChoice,
  type ConfirmedMapping,
} from "./ImportStates.js";
import { ERROR_GROUPS, GROUP_OF, type ErrorGroup } from "./import-errors.js";
import { importPollInterval, readStats, reviewFigures } from "./import-wizard.js";
import { saveBlob } from "./save-blob.js";

/** Rows shown for a group before the file takes over. */
const SAMPLE_ROWS = 25;

const EMPTY_SAMPLES: Readonly<Record<ErrorGroup, readonly ImportRowView[]>> = {
  check_digit: [],
  prefix: [],
  excel: [],
  values: [],
  duplicate: [],
};

export function ImportScreen({ importId }: { importId: string }): ReactNode {
  const client = useQueryClient();
  const detail = useQuery({
    queryKey: ["imports", importId],
    queryFn: () => api.importDetail(importId),
    refetchInterval: (query) => importPollInterval(query.state.data?.status),
  });
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });

  const reviewing = detail.data?.status === "review";
  const byKind = readStats(detail.data?.stats).byKind;
  const kinds = IMPORT_ERROR_KINDS.filter((k) => (byKind?.[k] ?? 0) > 0);
  const sampleQueries = useQueries({
    queries: kinds.map((kind) => ({
      queryKey: ["imports", importId, "rows", kind],
      queryFn: () => api.importRows({ id: importId, kind, limit: SAMPLE_ROWS }),
      enabled: reviewing,
    })),
  });
  const prefixes = useQuery({
    queryKey: ["prefixes"],
    queryFn: () => api.companyPrefixes(),
    enabled: reviewing && kinds.includes("outside_prefix"),
  });

  const settle = () => client.invalidateQueries({ queryKey: ["imports"] });
  const [refusal, setRefusal] = useState<string | null>(null);
  const refuse = (error: unknown) =>
    setRefusal(
      problemOf(error)?.detail ?? "That did not go through. Nothing was changed.",
    );

  const confirm = useMutation({
    mutationFn: (input: ConfirmedMapping) =>
      api.confirmImportMapping({ id: importId, ...input }),
    onSuccess: () => {
      setRefusal(null);
      return settle();
    },
    onError: refuse,
  });
  const apply = useMutation({
    mutationFn: (choice: ApplyChoice) => api.applyImportRows({ id: importId, ...choice }),
    onSuccess: settle,
    onError: refuse,
  });
  const cancel = useMutation({
    mutationFn: () => api.cancelImport(importId),
    onSuccess: settle,
    onError: refuse,
  });
  const download = useMutation({
    mutationFn: async (rows: "failed" | undefined) => {
      const report = await api.importReport({
        id: importId,
        ...(rows === undefined ? {} : { rows }),
      });
      saveBlob(new Blob([report.csv], { type: "text/csv" }), report.filename);
    },
    onError: refuse,
  });

  if (detail.isPending) return <p data-screen-label="Import loading">Loading…</p>;
  if (detail.error !== null) {
    return (
      <Surface tone="warn" emphasis="filled">
        <p data-screen-label="Import">
          {problemOf(detail.error)?.detail ??
            "We could not load this import. Reload to try again."}
        </p>
      </Surface>
    );
  }

  const samples = { ...EMPTY_SAMPLES } as Record<ErrorGroup, ImportRowView[]>;
  for (const group of ERROR_GROUPS) samples[group] = [];
  kinds.forEach((kind, i) => {
    samples[GROUP_OF[kind]].push(...(sampleQueries[i]?.data?.rows ?? []));
  });
  for (const group of ERROR_GROUPS) {
    samples[group] = [...new Map(samples[group].map((r) => [r.rowNo, r])).values()]
      .sort((a, b) => a.rowNo - b.rowNo)
      .slice(0, SAMPLE_ROWS);
  }

  return (
    <ImportFrame
      detail={detail.data}
      cancelling={cancel.isPending}
      onCancel={() => cancel.mutate()}
    >
      {refusal === null || detail.data.status === "mapping" ? null : (
        <Surface tone="warn" emphasis="filled">
          <p role="alert">{refusal}</p>
        </Surface>
      )}
      <StatusBody
        detail={detail.data}
        plan={org.data?.plan ?? "starter"}
        workspaces={org.data?.workspaces ?? []}
        samples={samples}
        prefixes={prefixes.data?.prefixes ?? []}
        confirming={confirm.isPending}
        applying={apply.isPending}
        downloading={download.isPending}
        refusal={refusal}
        onConfirm={(input) => confirm.mutate(input)}
        onApply={(choice) => apply.mutate(choice)}
        onDownload={(rows) => download.mutate(rows)}
      />
    </ImportFrame>
  );
}

/** One status, one body. The eight states and the three waits and ends around them. */
export function StatusBody({
  detail,
  plan,
  workspaces,
  samples,
  prefixes,
  confirming,
  applying,
  downloading,
  refusal,
  onConfirm,
  onApply,
  onDownload,
}: {
  detail: ImportDetail;
  plan: Parameters<typeof ImportOverLimit>[0]["plan"];
  workspaces: Parameters<typeof ImportClean>[0]["workspaces"];
  samples: Parameters<typeof ImportReview>[0]["samples"];
  prefixes: Parameters<typeof ImportReview>[0]["prefixes"];
  confirming: boolean;
  applying: boolean;
  downloading: boolean;
  refusal: string | null;
  onConfirm: (input: ConfirmedMapping) => void;
  onApply: (choice: ApplyChoice) => void;
  onDownload: (rows: "failed" | undefined) => void;
}): ReactNode {
  switch (detail.status) {
    case "uploaded":
    case "parsing":
      return <ImportReading detail={detail} />;
    case "mapping":
      return (
        <ImportMatching
          detail={detail}
          pending={confirming}
          refusal={refusal}
          onConfirm={onConfirm}
        />
      );
    case "validating":
      return <ImportChecking detail={detail} />;
    case "review": {
      const f = reviewFigures(detail);
      const clean = f.errors === 0;
      const over =
        detail.overLimit === null ? null : (
          <ImportOverLimit
            detail={detail}
            plan={plan}
            workspaces={workspaces}
            applying={applying}
            onApply={onApply}
          />
        );
      if (clean) {
        return (
          over ?? (
            <ImportClean
              detail={detail}
              workspaces={workspaces}
              applying={applying}
              onApply={onApply}
            />
          )
        );
      }
      return (
        <>
          {over}
          <ImportReview
            detail={detail}
            samples={samples}
            prefixes={prefixes}
            workspaces={workspaces}
            applying={applying}
            downloading={downloading}
            onApply={onApply}
            onDownload={() => onDownload(undefined)}
          />
        </>
      );
    }
    case "applying":
      return <ImportApplying detail={detail} />;
    case "done":
      return (
        <ImportDone detail={detail} downloading={downloading} onDownload={onDownload} />
      );
    case "failed":
    case "cancelled":
      return <ImportEnded detail={detail} />;
  }
}
